import { defineRenderNode, type RenderNodeDefsSubset } from './renderNodeDefs.js';
import { DEG2RAD, TWO_PI } from '../math/angle.js';




const SHAPE_OFFSET_JOIN_OPTIONS = [
  { label: 'Round', value: 'round' },
  { label: 'Miter', value: 'miter' },
  { label: 'Bevel', value: 'bevel' },
  { label: 'Square', value: 'square' },
] as const;

export const TRANSFORM_NODE_DEFS: RenderNodeDefsSubset<'Translate' | 'Rotate' | 'Scale' | 'ShapeTranslate' | 'ShapeRotate' | 'ShapeScale' | 'ShapeOffset' | 'ShapeAlongPath' | 'ShapeLattice' | 'ShapeBones'> = {
  Translate: defineRenderNode({
    label: 'Translate', cat: 'transform',
    inputs: [{ name: 'point', label: 'Point', type: 'vec2' }, { name: 'offset', label: 'Offset', type: 'vec2' }, { name: 'transform', label: 'Transform', type: 'transform' }],
    outputs: [{ name: 'result', label: 'Result', type: 'vec2' }, { name: 'transform', label: 'Transform', type: 'transform' }],
    graphs: ['material']
  }),
  Rotate: defineRenderNode({
    label: 'Rotate', cat: 'transform',
    inputs: [{ name: 'point', label: 'Point', type: 'vec2' }, { name: 'angle', label: 'Angle', type: 'float', range: { min: 0, max: TWO_PI, step: DEG2RAD, subtype: 'angle' } }, { name: 'transform', label: 'Transform', type: 'transform' }],
    outputs: [{ name: 'result', label: 'Result', type: 'vec2' }, { name: 'transform', label: 'Transform', type: 'transform' }],
    graphs: ['material']
  }),
  Scale: defineRenderNode({
    label: 'Scale', cat: 'transform',
    inputs: [
      { name: 'point', label: 'Point', type: 'vec2' },
      { name: 'default', label: 'Scale', type: 'float', range: { min: 0, max: 100, step: 0.01, subtype: 'factor' } },
      { name: 'scaleX', label: 'Scale X', type: 'float', range: { min: 0, max: 100, step: 0.01, subtype: 'factor' }, optional: true },
      { name: 'scaleY', label: 'Scale Y', type: 'float', range: { min: 0, max: 100, step: 0.01, subtype: 'factor' }, optional: true },
      { name: 'transform', label: 'Transform', type: 'transform' },
    ],
    outputs: [
      { name: 'result', label: 'Result', type: 'vec2' },
      { name: 'scaleX', label: 'Scale X', type: 'float' },
      { name: 'scaleY', label: 'Scale Y', type: 'float' },
      { name: 'transform', label: 'Transform', type: 'transform' },
    ],
    graphs: ['material']
  }),
  ShapeTranslate: defineRenderNode({
    label: 'Translate', cat: 'transform',
    inputs: [
      { name: 'shape', label: 'Shape', type: 'shape' },
      { name: 'offset', label: 'Offset', type: 'vec2' },
    ],
    outputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    graphs: ['geometry']
  }),
  ShapeRotate: defineRenderNode({
    label: 'Rotate', cat: 'transform',
    inputs: [
      { name: 'shape', label: 'Shape', type: 'shape' },
      { name: 'angle', label: 'Angle', type: 'float', range: { min: 0, max: TWO_PI, step: DEG2RAD, subtype: 'angle' } },
    ],
    outputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    graphs: ['geometry']
  }),
  ShapeScale: defineRenderNode({
    label: 'Scale', cat: 'transform',
    inputs: [
      { name: 'shape', label: 'Shape', type: 'shape' },
      { name: 'default', label: 'Scale', type: 'float', range: { min: 0, max: 100, step: 0.01, subtype: 'factor' } },
      { name: 'scaleX', label: 'Scale X', type: 'float', range: { min: 0, max: 100, step: 0.01, subtype: 'factor' }, optional: true },
      { name: 'scaleY', label: 'Scale Y', type: 'float', range: { min: 0, max: 100, step: 0.01, subtype: 'factor' }, optional: true },
    ],
    outputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    graphs: ['geometry']
  }),
  // The exact offset of the resolved shape, outward for a positive distance;
  // `join` turns its corners, and `miter` and `square` read the limit
  // (nodes.rs SHAPE_OFFSET_JOINS).
  ShapeOffset: defineRenderNode({
    label: 'Offset', cat: 'transform',
    inputs: [
      { name: 'shape', label: 'Shape', type: 'shape' },
      { name: 'distance', label: 'Distance', type: 'float', range: { min: -512, max: 512, step: 0.5, subtype: 'pixels' } },
      { name: 'miterLimit', label: 'Miter limit', type: 'float', range: { min: 1, max: 100, step: 0.1 } },
    ],
    outputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    graphs: ['geometry'],
    ui: [
      { kind: 'port', ref: 'shape', io: 'in' },
      { kind: 'port', ref: 'distance' },
      { kind: 'select', field: 'join', label: 'Join', options: SHAPE_OFFSET_JOIN_OPTIONS, default: 'round' },
      { kind: 'port', ref: 'miterLimit' },
      { kind: 'port', ref: 'shape', io: 'out' },
    ],
  }),
  // The shape deformed by what the node's data holds -- a path, a lattice, bones
  // (fluveria-engine FORMAT.md, kb:task/240, kb:task/426): posed in the native
  // editor, carried here as the document has it.
  ShapeAlongPath: deformNode('Along path'),
  ShapeLattice: deformNode('Lattice'),
  ShapeBones: deformNode('Bones'),
};

function deformNode(label: string) {
  return defineRenderNode({
    label, cat: 'transform',
    inputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    outputs: [{ name: 'shape', label: 'Shape', type: 'shape' }],
    graphs: ['geometry'],
  });
}
